import { useEffect, useState, useCallback, useRef } from 'react';
import { z } from 'zod';
import { errorCode } from '../utils/abortVariants';

// -- Message types --

/**
 * Init message sent by the parent to Ronan via postMessage. `intentId` and
 * `nonce` are optional; the rest are required for the handshake. `webhookUri`
 * is the server-to-server callback the parent asks the auth code to be
 * delivered to.
 */
const initMessageSchema = z.object({
  type: z.literal('ronan:init'),
  clientId: z.string().min(1),
  scope: z.string().min(1),
  state: z.string().min(1),
  codeChallenge: z.string().min(1),
  webhookUri: z.string().min(1),
  intentId: z.string().optional(),
  nonce: z.string().optional(),
});

export type RonanInitMessage = z.infer<typeof initMessageSchema>;

/**
 * The messages that manage the iframe's box, between Ronan and the
 * embedded loader (`src/embedded.ts`). The flow's result never travels here:
 * it reaches the website's backend through the webhook.
 *
 * The loader imports these with `import type`, so its bundle stays a
 * dependency-free classic script.
 */

/** How embedded Ronan is shown: an inline card, or a full-screen drawer (phones). */
export type EmbeddedMode = 'card' | 'drawer';

/** Ronan → loader. Carries no flow data, so it is posted to any parent. */
export type RonanToLoaderMessage =
  /** Sent once Ronan listens for `ronan:init`; the loader sends init once the website opens the flow (`verid:open`). */
  | { type: 'ronan:mode'; mode: EmbeddedMode }
  /**
   * Sent once the flow's start result is applied (its appearance set) or an
   * error is shown. Only then does the loader show the iframe, so the card
   * never appears in the default look and then changes.
   */
  | { type: 'ronan:show' }
  /** Card mode: the card's height in CSS pixels. */
  | { type: 'ronan:resize'; height: number }
  /** Drawer mode: the person closed the drawer. The loader hides the iframe. */
  | { type: 'ronan:close' };

/**
 * Ronan → the loader: the flow's lifecycle, which the loader fires on the
 * page as `verid:ready` / `verid:complete` / `verid:error` / `verid:cancel`.
 * Never carries the result (no code, claims or outcome): that reaches the
 * website's backend only through the webhook. Posted to the init sender's
 * origin, never to any parent: `ronan:error` always, the others only once
 * that origin is verified.
 */
export type RonanLifecycleMessage =
  /** The flow has started and can be shown. */
  | { type: 'ronan:ready' }
  /** The flow finished; the backend calls the webhook. */
  | { type: 'ronan:complete' }
  | { type: 'ronan:error'; error: string }
  | { type: 'ronan:cancel' };

/** Loader → Ronan, besides `ronan:init`. */
export type LoaderToRonanMessage =
  /** Drawer mode: the website opened the flow (`verid:open`); Ronan opens the drawer. */
  | { type: 'ronan:open' }
  /**
   * Drawer mode: the loader closed the drawer itself (Escape outside Ronan,
   * the Android back gesture); Ronan closes its sheet, so the next open
   * slides it up and focuses it again.
   */
  | { type: 'ronan:hide' };

// -- Hook --

/**
 * Set while embedded. Kept outside React so the error boundaries can still
 * tell the page after a crash unmounted the hook.
 */
let reportCrash: (() => void) | null = null;

/** Ronan crashed: `ronan:error` to the page that sent `ronan:init`. */
export function reportCrashToParent() {
  reportCrash?.();
}

/**
 * useParentMessaging — embedded Ronan's init handshake.
 *
 * Protocol:
 * 1. The loader sends `ronan:init` with the flow parameters.
 * 2. Ronan records `event.origin`.
 * 3. After reading the interaction context, Ronan verifies the saved origin
 *    against the server-supplied `allowedEmbedOrigins`.
 * 4. `notify` posts lifecycle messages to that origin only. `ronan:error`
 *    goes whether or not the origin is verified, so a refused or malformed
 *    init still hears why the flow did not start; `ronan:ready`,
 *    `ronan:complete` and `ronan:cancel` only once it is verified.
 *
 * Ronan reports no result to the page: it reaches the website's backend
 * through the webhook.
 */
export function useParentMessaging(isEmbedded: boolean) {
  const [verifiedOrigin, setVerifiedOrigin] = useState<string | null>(null);
  const [initData, setInitData] = useState<RonanInitMessage | null>(null);
  const [handshakeError, setHandshakeError] = useState<string | null>(null);
  // notify runs from async journey code, after re-renders: it reads refs.
  const originRef = useRef<string | null>(null);
  const allowedRef = useRef(false);
  const sentRef = useRef({ ready: false, ended: false });
  const initRef = useRef(false);

  useEffect(() => {
    if (!isEmbedded) return;

    const handler = (event: MessageEvent) => {
      // Only the loader, in the page that embeds Ronan, sends the flow, and
      // only once.
      if (event.source !== window.parent || initRef.current) return;
      const data = event.data;
      if (!data || data.type !== 'ronan:init') return;

      const parsed = initMessageSchema.safeParse(data);
      if (!parsed.success) {
        const missing = parsed.error.issues
          .map((issue) => issue.path.join('.'))
          .join(', ');
        const error = `[Ronan] ronan:init is missing or has invalid required fields: ${missing}`;
        console.error(error, data);
        // The sender still hears the error.
        originRef.current = event.origin;
        setHandshakeError(error);
        return;
      }

      initRef.current = true;
      setInitData(parsed.data);
      // Save the origin before verification — checked later via verifyOrigin.
      originRef.current = event.origin;
      setVerifiedOrigin(event.origin);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isEmbedded]);

  /**
   * Post a lifecycle message to the init sender's origin. `ronan:ready` goes
   * once, and only one of complete, cancel and error ends the flow. Dropped
   * when not embedded or before `ronan:init`, and all but `ronan:error` until
   * the origin is verified.
   */
  const notify = useCallback(
    (message: RonanLifecycleMessage) => {
      const origin = originRef.current;
      if (!isEmbedded || !origin) return;
      if (message.type !== 'ronan:error' && !allowedRef.current) return;
      const sent = sentRef.current;
      if (sent.ended) return;
      if (message.type === 'ronan:ready') {
        if (sent.ready) return;
        sent.ready = true;
      } else {
        sent.ended = true;
      }
      window.parent.postMessage(message, origin);
    },
    [isEmbedded],
  );

  useEffect(() => {
    if (!isEmbedded) return;
    reportCrash = () =>
      notify({ type: 'ronan:error', error: errorCode('serverError') });
  }, [isEmbedded, notify]);

  /**
   * Verify the saved parent origin against the server-supplied
   * `allowedEmbedOrigins`. Returns true if allowed; otherwise returns false,
   * and the page hears only the refusal's `ronan:error`. The caller shows the
   * refusal (`handshakeError` is for a bad `ronan:init`).
   */
  const verifyOrigin = useCallback(
    (allowedEmbedOrigins: string[]): boolean => {
      if (!verifiedOrigin) return false;
      if (!allowedEmbedOrigins.includes(verifiedOrigin)) {
        console.error(
          `[Ronan] Parent origin "${verifiedOrigin}" is not in the allowed origins list.`,
        );
        setVerifiedOrigin(null);
        return false;
      }
      allowedRef.current = true;
      return true;
    },
    [verifiedOrigin],
  );

  return { verifiedOrigin, initData, handshakeError, verifyOrigin, notify };
}
